import { useEffect, useMemo, useRef } from 'react';
import { activePresetKey } from './period';

// The preset behind a page's date range, decided when the range CHANGES — and
// kept rolling while the page stays mounted.
//
// Two failures this closes, both from the /credits field report (28/09/2026):
//
// 1. Deciding "is this a preset?" at SAVE time compares yesterday's dates with
//    today's preset and answers no — so a page open across midnight (or a month
//    boundary on /suppliers) re-saved its preset as frozen literal dates the
//    next time any other filter moved. The key is therefore derived only when
//    dateFrom/dateTo change, at which moment they were just computed and are
//    fresh; a later day change leaves the key standing. Save it via
//    toStoredPeriod / withPeriodParams, never by re-deriving.
//
// 2. A tab left open for days never re-enters the page, so fixing only the
//    restore-on-entry path left the mounted page showing the day-it-opened
//    range. When the tab becomes visible / focused again and the preset now
//    means different dates, `onRoll(from, to)` is called with them.
//    Deliberately NOT on a midnight timer: a table must not shift under
//    someone who is actively working in it — the next time they come back to
//    the tab is the natural moment.
//
// `presets` must be a stable (module-level) array — the preset list the page's
// PeriodPicker offers. Returns the active preset key, or null.
export function usePresetPeriod(dateFrom, dateTo, presets, onRoll) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const presetKey = useMemo(() => activePresetKey(dateFrom, dateTo, presets), [dateFrom, dateTo]);

  const latest = useRef(null);
  latest.current = { presetKey, dateFrom, dateTo, onRoll };

  useEffect(() => {
    const roll = () => {
      if (document.visibilityState === 'hidden') return;
      const { presetKey: key, dateFrom: from, dateTo: to, onRoll: apply } = latest.current;
      const preset = key && presets.find(p => p.key === key);
      if (!preset) return;
      const fresh = preset.range();
      if (fresh.from !== from || fresh.to !== to) apply(fresh.from, fresh.to);
    };
    document.addEventListener('visibilitychange', roll);
    window.addEventListener('focus', roll);
    return () => {
      document.removeEventListener('visibilitychange', roll);
      window.removeEventListener('focus', roll);
    };
  }, [presets]);

  return presetKey;
}
